import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Bell } from "lucide-react";
import { listMySubscriptions } from "@/lib/forum-extra.functions";

export const Route = createFileRoute("/_authenticated/forum/bevakningar")({
  head: () => ({
    meta: [
      { title: "Mina bevakningar – Fritidsforum | AK Husbil & Fritid" },
      { name: "description", content: "Se och hantera de trådar och kategorier du bevakar i Fritidsforum." },
      { property: "og:title", content: "Mina bevakningar – Fritidsforum" },
      { property: "og:description", content: "Hantera trådar och kategorier du bevakar i communityn." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Subscriptions,
});

function Subscriptions() {
  const fn = useServerFn(listMySubscriptions);
  const { data, isLoading } = useQuery({ queryKey: ["forum", "my-subscriptions"], queryFn: () => fn() });

  const threads = data?.threads ?? [];
  const categories = data?.categories ?? [];

  return (
    <section className="bg-warm">
      <div className="container-edge py-10 md:py-16">
        <Link to="/forum" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={15} /> Fritidsforum
        </Link>

        <h1 className="mt-6 font-display text-4xl leading-tight md:text-5xl">Mina bevakningar</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Du får en notis i klockan så fort någon svarar i det du bevakar.
        </p>

        {isLoading && <p className="mt-8 text-sm text-muted-foreground">Hämtar…</p>}

        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl">Trådar</h2>
            <ul className="mt-4 space-y-2">
              {threads.map((t) => (
                <li key={t.id} className="rounded-2xl border border-border bg-card p-4">
                  <Link
                    to="/forum/t/$slug"
                    params={{ slug: t.slug }}
                    className="text-sm font-semibold leading-snug hover:text-accent"
                  >
                    {t.title}
                  </Link>
                  <p className="mt-1 text-xs text-muted-foreground">{t.comment_count} svar</p>
                </li>
              ))}
              {!threads.length && !isLoading && (
                <li className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                  Inga bevakade trådar ännu. Tryck <Bell size={12} className="inline" /> Bevaka i en tråd.
                </li>
              )}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl">Kategorier</h2>
            <ul className="mt-4 space-y-2">
              {categories.map((c) => (
                <li key={c.id} className="rounded-2xl border border-border bg-card p-4">
                  <Link
                    to="/forum/k/$slug"
                    params={{ slug: c.slug }}
                    className="text-sm font-semibold hover:text-accent"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
              {!categories.length && !isLoading && (
                <li className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                  Bevaka en hel kategori för att aldrig missa nya trådar.
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
